import Tone from 'tone';
import Track from 'Track';
import {ROLL_EFFECT} from 'effects/live/constants.js';
const TIME_16N = new Tone.Time('16n');

class RecordedTrigger {
    constructor(track, sliceNumber, resolution, sequencerEngine, quantized, velocity=1) {
        this.sequencerEngine = sequencerEngine;

        this.track = track;
        this._velocity = velocity;
        // store information on when the trigger started
        this.startTime = Tone.Master.context.currentTime;
        this.currentStep = sequencerEngine.getCurrentStep(resolution, track);
        this.currentGlobalStep = sequencerEngine.lastTrackStep;

        this.currentStepUnquantized = sequencerEngine.trackStepReal;
        this.ogCurrentStepUnquantized = this.currentStepUnquantized;
        this.currentStepTime = sequencerEngine.currentStepTime;

        if (!track.subSequence.isTrack) {
            this.currentStepUnquantized = this.track.getPatternStep(this.currentStepUnquantized);
        } 
        
        this.currentStep = this.track.getPatternStep(this.currentStep);
        this.currentGlobalStep = this.track.getPatternStep(this.currentGlobalStep);

        this.quantized = quantized;

        this.trackNumber = track ? track.trackNumber : 0;
        this.sliceNumber = sliceNumber;
        this.resolution = resolution;
        this.rolling = track ? track.rolling : false;
    }

    get velocity() {
        return this._velocity === null ?
            this.track.trackData.velocity : this._velocity;
    }
    
    getDuration(quantized=this.quantized) {
        let time = Tone.Master.context.currentTime;
        let duration = undefined;
        let durationSixteenths = undefined;
        let sixteenth = TIME_16N.toMilliseconds() / 1000;
        if ((this.currentStepTime === undefined ||
             Math.abs(this.currentStepTime - this.startTime) > sixteenth) && quantized) {
            let currentGlobalStep = Math.max(this.currentGlobalStep, 0);
            durationSixteenths = (this.sequencerEngine.lastTrackStep - currentGlobalStep) / 2;
            duration = durationSixteenths * TIME_16N.toMilliseconds();
        } else {
            duration = new Tone.Time(time).toMilliseconds() -
                new Tone.Time(quantized ? this.currentStepTime : this.startTime).toMilliseconds();
            durationSixteenths = duration / TIME_16N.toMilliseconds();
        }
        return {
            duration: duration,
            durationSixteenths: durationSixteenths,
        };
    }

    record(sequencerEngine, track, transpose) {
        track = track || this.track;
        transpose = transpose || track.trackData.transpose;
        sequencerEngine = sequencerEngine || track.sequencerEngine;
        let durationObj = this.getDuration();
        let duration = durationObj.duration;
        let durationSixteenths = durationObj.durationSixteenths;
        var timeFromStep =
            new Tone.Time(this.startTime).toMilliseconds() -
            new Tone.Time(this.currentStepTime).toMilliseconds();
        
        var thirtySeconds = new Tone.Time('32n').toMilliseconds();
        var bucketKey = timeFromStep / thirtySeconds;

        // sometimes for some weird race condition-y reason, the timeFromStep can be negative
        // so we choose the previous step and adjust the bucket key
        /*
        if (timeFromStep < 0) {
            bucketKey = 1 + bucketKey;
            if (this.currentStepUnquantized == 0) {
                this.currentStepUnquantized = 31;;
            } else {
                this.currentStepUnquantized--;
            }
        }
        */

        if (this.quantized) {
            bucketKey = Math.max(0, bucketKey);
            let s = this.currentStepUnquantized + bucketKey;
            s = this.resolution.transformStepAtResolution2(s, Track.baseResolution);
            s = Math.round(s);
            if (!track.subSequence.isTrack) {
                s = s % this.track.pattern.length(this.resolution);
            }
            if (isNaN(s)) {
                s = 0;
            }
            track.recordHit(
                s, this.resolution, this.sliceNumber, durationSixteenths, transpose, this.velocity, this.release);
        } else {
            var timeFromStep =
                new Tone.Time(this.startTime).toMilliseconds() -
                new Tone.Time(this.currentStepTime).toMilliseconds();

            var thirtySeconds = new Tone.Time('32n').toMilliseconds();

           if (this.rolling) {
                let numRolls = (this.getNumberOfRollsForDuration(duration, track));
                let offset = 32 / this.resolution.numSteps();
                let rollDuration = this.getDurationOfRollTime(track);
                for (let i=this.currentStepUnquantized + bucketKey; i <= this.currentStepUnquantized + (offset*numRolls); i+= offset) {
                    let stepNumber = Math.floor(i);
                    let newBucketKey = i - stepNumber;
                    
                    track.recordUnquantizedHit(
                        stepNumber, this.sliceNumber, rollDuration, newBucketKey, transpose, this.velocity);
                }
            } else {
                track.recordUnquantizedHit(
                    this.ogCurrentStepUnquantized, this.sliceNumber, durationSixteenths, bucketKey, transpose, this.velocity, this.release);

            }
        }
    }

    getDurationOfRollTime(track) {
        let resolution = this.resolution;
        return resolution.getTimeInStep().toMilliseconds() /
            TIME_16N.toMilliseconds() ;
    }

    getStepsInRollTime(track) {
        // each step is 32th note
        let resolution = this.resolution;
        let coeff = resolution.numSteps()/resolution.baseResolution;
        let rollTime = resolution.numSteps();
        if (rollTime === 32) {
            return coeff;
        } else if (rollTime === 48) {
            return coeff;
        } else if (rollTime === 16) {
            return 2 * coeff;
        } else if (rollTime == 24) {
            return 2 * coeff;
        } else if (rollTime === 8) {
            return 4 * coeff;
        } else if (rollTime === 12) {
            return 4 * coeff; 
        } else if (rollTime === 4) {
            return 8 * coeff;
        } else if (rollTime == 6) {
            return 8 * coeff;
        }
        return null;
    }

    getRollResolution(track) {
        let rollEffect = track.liveEffects.getEffect(ROLL_EFFECT);
        let resolution = this.resolution;
        if (rollEffect) { 
            resolution = resolution.getCopy();
            resolution.resolution *= rollEffect.rollFactor;
        }
        return resolution;
    }
    
    getNumberOfRollsForDuration(duration, track) {
        let resolution = this.resolution;
        return duration / resolution.getTimeInStep().toMilliseconds();
    }
}

export default RecordedTrigger;
